import { LogItem, LogTimeByDateResponse } from "@lhd/types";
import { LOG_TYPE } from "@lhd/features/TrackingTime/HomeTracking/contants";
import { dayMemoFromApiLogtimes } from "@lhd/features/TrackingTime/dayMemoFromApiLogtimes";
import {
  applyImplicitBreakEndToFormValues,
  BREAK_END_AUTOFILLED_KEY,
  hasVirtualLogtimesFormInput,
  remapFormKeysAfterCreate,
  rowHasVirtualUserTimeInput,
} from "@lhd/features/TrackingTime/DetailTracking/createVirtualLogtimesBeforeSave";
import { buildTrackingTimelineRows } from "@lhd/features/TrackingTime/timelineRows";
import dayjs from "dayjs";

const LOGGED_AT_API_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";

export const toLoggedAtIso = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  if (typeof value === "string") return value;
  if (dayjs.isDayjs(value)) return value.format(LOGGED_AT_API_FORMAT);
  return null;
};

const resolveAttachmentId = (item: unknown): string | number | null => {
  if (item == null || typeof item !== "object" || item instanceof File)
    return null;
  const o = item as Record<string, unknown>;
  const candidates = [o.id, o.file_id, o.attachment_id, o.logtime_file_id];
  for (const candidate of candidates) {
    if (
      typeof candidate === "number" &&
      Number.isFinite(candidate) &&
      candidate > 0
    ) {
      return candidate;
    }
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
};

const isNullOrBlank = (value: unknown): boolean => {
  if (value == null) return true;
  return String(value).trim().length === 0;
};

const normMemoCompare = (s: string): string =>
  s.replace(/\r\n/g, "\n").trimEnd();

const trimmedOrNull = (value: unknown): string | null => {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
};

const loggedAtToComparableMs = (iso: string | null): number | null => {
  if (iso == null) return null;
  const ms = dayjs(iso).valueOf();
  return Number.isFinite(ms) ? ms : null;
};

type LogtimePatch = {
  logged_at?: unknown;
  note?: unknown;
  attachments?: unknown[];
};

type SaveBuildContext = {
  memoRaw: string;
  dayMemo: string;
  userEditedMemo: boolean;
  hasAnyPersistedLogtime: boolean;
  persistedNoteLogtimes: LogItem[];
  rows: LogItem[];
  sharedNote: string;
  shouldAttachNoteToRow: boolean;
  shouldIncludeNoteOnEditedRows: boolean;
};

const buildSaveContext = (
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
): SaveBuildContext => {
  const memoRaw = String(formValues.memo ?? "");
  const dayMemo = memoRaw.trim();
  const serverMemoAggregate = dayMemoFromApiLogtimes(data.logtimes);
  const userEditedMemo =
    normMemoCompare(memoRaw) !== normMemoCompare(serverMemoAggregate);

  const hasAnyPersistedLogtime = (data.logtimes ?? []).some(
    (lt) => typeof lt.id === "number" && lt.id > 0,
  );
  const persistedNoteLogtimes = (data.logtimes ?? []).filter(
    (lt) => lt.log_type === "note" && typeof lt.id === "number" && lt.id > 0,
  );
  const rows = buildTrackingTimelineRows(data);

  const resolveRowNote = (
    patch: LogtimePatch | undefined,
    lt: { note: string | null },
  ): string => {
    if (userEditedMemo) return memoRaw;
    if (
      patch != null &&
      typeof patch.note === "string" &&
      patch.note.trim() !== ""
    ) {
      return patch.note;
    }
    return String(lt.note ?? "");
  };

  const resolveSharedNote = (): string => {
    if (userEditedMemo) return memoRaw;
    if (dayMemo !== "") return dayMemo;

    for (const row of rows) {
      const patch = formValues[String(row.id)] as LogtimePatch | undefined;
      const rowNote = resolveRowNote(patch, row);
      if (!isNullOrBlank(rowNote)) return rowNote;
    }

    return "";
  };

  const sharedNote = resolveSharedNote();
  const shouldAttachNoteToRow = !isNullOrBlank(sharedNote);

  return {
    memoRaw,
    dayMemo,
    userEditedMemo,
    hasAnyPersistedLogtime,
    persistedNoteLogtimes,
    rows,
    sharedNote,
    shouldAttachNoteToRow,
    shouldIncludeNoteOnEditedRows: userEditedMemo || shouldAttachNoteToRow,
  };
};

const resolveFormLoggedAt = (
  rowId: number,
  formValues: Record<string, unknown>,
): string | null =>
  trimmedOrNull(
    toLoggedAtIso(
      (formValues[String(rowId)] as LogtimePatch | undefined)?.logged_at,
    ),
  );

const resolveEffectiveLoggedAtForJsonSave = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): string | null => {
  const fromForm = resolveFormLoggedAt(lt.id, formValues);
  if (fromForm) return fromForm;
  if (typeof lt.id === "number" && lt.id > 0) {
    return trimmedOrNull(lt.logged_at);
  }
  return null;
};

const rowHasPersistedTimeMutation = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): boolean => {
  if (typeof lt.id !== "number" || lt.id <= 0) return false;
  const serverLoggedAt = trimmedOrNull(lt.logged_at);
  const formLoggedAt = resolveFormLoggedAt(lt.id, formValues);
  return (
    loggedAtToComparableMs(formLoggedAt) !==
    loggedAtToComparableMs(serverLoggedAt)
  );
};

const isImplicitBreakEndRow = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): boolean => {
  if (lt.log_type !== LOG_TYPE.BREAK_END) return false;
  const patch = formValues[String(lt.id)] as
    | Record<string, unknown>
    | undefined;
  return patch?.[BREAK_END_AUTOFILLED_KEY] === true;
};

const shouldIncludeVirtualRowInJsonPayload = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): boolean =>
  rowHasVirtualUserTimeInput(lt, formValues) ||
  isImplicitBreakEndRow(lt, formValues);

const hasWorkerLogtimesJsonTimeChanges = (
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
): boolean => {
  const ctx = buildSaveContext(data, formValues);
  if (ctx.userEditedMemo) return true;
  return ctx.rows.some((lt) => {
    if (lt.log_type === LOG_TYPE.NOTE) return false;
    if (typeof lt.id === "number" && lt.id < 0) {
      return shouldIncludeVirtualRowInJsonPayload(lt, formValues);
    }
    return rowHasPersistedTimeMutation(lt, formValues);
  });
};

const resolveRowAttachmentMutation = (
  lt: LogItem,
  patch: LogtimePatch | undefined,
): {
  deletedByDiff: Array<string | number>;
  newFiles: File[];
  hasAttachmentMutation: boolean;
} => {
  let deletedByDiff: Array<string | number> = [];
  let newFiles: File[] = [];

  if (lt.log_type === LOG_TYPE.CHECKIN || lt.log_type === LOG_TYPE.CHECKOUT) {
    const patchAttachments = patch?.attachments;
    const currentAttachmentIds = Array.isArray(patchAttachments)
      ? new Set(
          patchAttachments
            .map((att: unknown) => resolveAttachmentId(att))
            .filter((id): id is string | number => id != null)
            .map((id) => String(id)),
        )
      : null;

    deletedByDiff =
      currentAttachmentIds && Array.isArray(lt.attachments)
        ? lt.attachments
            .map((att: unknown) => resolveAttachmentId(att))
            .filter((id): id is string | number => id != null)
            .filter((id) => !currentAttachmentIds.has(String(id)))
        : [];

    const atts = patch?.attachments;
    newFiles = Array.isArray(atts)
      ? atts.filter((att): att is File => att instanceof File)
      : [];
  }

  return {
    deletedByDiff,
    newFiles,
    hasAttachmentMutation: deletedByDiff.length > 0 || newFiles.length > 0,
  };
};

export type WorkerLogtimeSaveJsonItem = {
  id?: number;
  log_type?: LogItem["log_type"];
  logged_at?: string;
  note?: string;
};

export type WorkerLogtimesSaveJsonPayload = {
  worker_id: number;
  company_id: string;
  date: string;
  logtimes: WorkerLogtimeSaveJsonItem[];
};

export type BuildWorkerLogtimesSaveJsonResult = {
  payload: WorkerLogtimesSaveJsonPayload;
  hasJsonPayload: boolean;
};

export function buildWorkerLogtimesSaveJsonPayload(
  companyId: string,
  workerId: number,
  workDate: string,
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
): BuildWorkerLogtimesSaveJsonResult {
  applyImplicitBreakEndToFormValues(data, formValues);
  const ctx = buildSaveContext(data, formValues);
  const logtimes: WorkerLogtimeSaveJsonItem[] = [];
  const noteSyncedIds = new Set<number>();

  ctx.rows.forEach((lt) => {
    if (lt.log_type === LOG_TYPE.NOTE) return;

    const isVirtualRow =
      typeof lt.id === "number" && Number.isFinite(lt.id) && lt.id < 0;
    const hasPersistedId = typeof lt.id === "number" && lt.id > 0;

    if (isVirtualRow) {
      if (!shouldIncludeVirtualRowInJsonPayload(lt, formValues)) return;
      const loggedAt = resolveEffectiveLoggedAtForJsonSave(lt, formValues);
      if (!loggedAt) return;
      const item: WorkerLogtimeSaveJsonItem = {
        log_type: lt.log_type,
        logged_at: loggedAt,
      };
      if (ctx.shouldIncludeNoteOnEditedRows) {
        item.note = ctx.sharedNote;
      }
      logtimes.push(item);
      return;
    }

    if (!hasPersistedId) return;

    const loggedAt = resolveEffectiveLoggedAtForJsonSave(lt, formValues);
    if (!loggedAt) return;

    const hasTimeMutation = rowHasPersistedTimeMutation(lt, formValues);
    const item: WorkerLogtimeSaveJsonItem = {
      id: lt.id,
      logged_at: loggedAt,
    };
    if (ctx.shouldIncludeNoteOnEditedRows && hasTimeMutation) {
      item.note = ctx.sharedNote;
      noteSyncedIds.add(lt.id);
    }
    logtimes.push(item);
  });

  if (ctx.userEditedMemo) {
    for (const lt of data.logtimes ?? []) {
      if (typeof lt.id !== "number" || lt.id <= 0) continue;
      if (noteSyncedIds.has(lt.id)) continue;

      const item: WorkerLogtimeSaveJsonItem = {
        id: lt.id,
        note: ctx.sharedNote,
      };
      if (lt.log_type === LOG_TYPE.NOTE) {
        item.log_type = LOG_TYPE.NOTE;
      }
      logtimes.push(item);
      noteSyncedIds.add(lt.id);
    }
  }

  if (!ctx.hasAnyPersistedLogtime && !isNullOrBlank(ctx.sharedNote)) {
    logtimes.push({
      log_type: LOG_TYPE.NOTE,
      note: ctx.sharedNote,
    });
  } else if (
    ctx.hasAnyPersistedLogtime &&
    ctx.persistedNoteLogtimes.length === 0 &&
    ctx.shouldAttachNoteToRow &&
    ctx.userEditedMemo &&
    noteSyncedIds.size === 0
  ) {
    logtimes.push({
      log_type: LOG_TYPE.NOTE,
      note: ctx.sharedNote,
    });
  }

  return {
    payload: {
      worker_id: workerId,
      company_id: companyId,
      date: workDate,
      logtimes,
    },
    hasJsonPayload: logtimes.length > 0,
  };
}

export type BuildWorkerLogtimesSaveFormDataOptions = {
  attachmentsOnly?: boolean;
};

export type BuildWorkerLogtimesSaveFormDataResult = {
  formData: FormData;
  hasLogtimePayload: boolean;
};

export function buildWorkerLogtimesSaveFormData(
  companyId: string,
  workerId: number,
  workDate: string,
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
  canEditAllAttendanceFields?: boolean,
  options?: BuildWorkerLogtimesSaveFormDataOptions,
): BuildWorkerLogtimesSaveFormDataResult {
  const attachmentsOnly = options?.attachmentsOnly === true;
  const formData = new FormData();
  formData.append("company_id", companyId);
  formData.append("worker_id", String(workerId));
  formData.append("date", workDate);

  if (!attachmentsOnly && canEditAllAttendanceFields) {
    applyImplicitBreakEndToFormValues(data, formValues);
  }

  const ctx = buildSaveContext(data, formValues);
  let appendedAnyLogtimeItem = false;
  const noteSyncedIds = new Set<number>();

  ctx.rows.forEach((lt) => {
    const patch = formValues[String(lt.id)] as LogtimePatch | undefined;
    const isVirtualRow =
      typeof lt.id === "number" && Number.isFinite(lt.id) && lt.id < 0;
    const hasPersistedId = typeof lt.id === "number" && lt.id > 0;
    if (isVirtualRow || !hasPersistedId) return;

    const serverLoggedAt = trimmedOrNull(lt.logged_at);
    const formLoggedAt = trimmedOrNull(toLoggedAtIso(patch?.logged_at));
    const hasTimeMutation =
      !attachmentsOnly &&
      !!canEditAllAttendanceFields &&
      loggedAtToComparableMs(formLoggedAt) !==
        loggedAtToComparableMs(serverLoggedAt);

    const { deletedByDiff, newFiles, hasAttachmentMutation } =
      resolveRowAttachmentMutation(lt, patch);

    if (!hasTimeMutation && !hasAttachmentMutation) {
      return;
    }

    if (hasTimeMutation || hasAttachmentMutation) {
      formData.append(`logtimes[][id]`, String(lt.id));
      appendedAnyLogtimeItem = true;
    }
    if (hasAttachmentMutation && !hasTimeMutation) {
      formData.append(`logtimes[][edit_logtime_at]`, "true");
    }
    if (hasTimeMutation) {
      formData.append(
        `logtimes[][logged_at]`,
        formLoggedAt != null ? formLoggedAt : "",
      );
    }
    if (
      !attachmentsOnly &&
      (hasTimeMutation || hasAttachmentMutation) &&
      ctx.shouldIncludeNoteOnEditedRows
    ) {
      formData.append(`logtimes[][note]`, ctx.sharedNote);
      noteSyncedIds.add(lt.id);
    }

    deletedByDiff.forEach((delId) => {
      formData.append(`logtimes[][file_deleted_ids][]`, String(delId));
      appendedAnyLogtimeItem = true;
    });
    newFiles.forEach((file) => {
      formData.append(`logtimes[][files][]`, file, file.name);
      appendedAnyLogtimeItem = true;
    });
  });

  if (!attachmentsOnly && ctx.userEditedMemo) {
    for (const lt of data.logtimes ?? []) {
      if (typeof lt.id !== "number" || lt.id <= 0) continue;
      if (noteSyncedIds.has(lt.id)) continue;

      formData.append("logtimes[][id]", String(lt.id));
      if (lt.log_type === LOG_TYPE.NOTE) {
        formData.append("logtimes[][log_type]", LOG_TYPE.NOTE);
      }
      formData.append("logtimes[][note]", ctx.sharedNote);
      appendedAnyLogtimeItem = true;
      noteSyncedIds.add(lt.id);
    }
  }

  if (
    !attachmentsOnly &&
    !ctx.hasAnyPersistedLogtime &&
    !isNullOrBlank(ctx.sharedNote)
  ) {
    formData.append("logtimes[][log_type]", LOG_TYPE.NOTE);
    formData.append("logtimes[][note]", ctx.sharedNote);
    appendedAnyLogtimeItem = true;
  } else if (
    !attachmentsOnly &&
    ctx.hasAnyPersistedLogtime &&
    ctx.persistedNoteLogtimes.length === 0 &&
    ctx.shouldAttachNoteToRow &&
    ctx.userEditedMemo &&
    noteSyncedIds.size === 0
  ) {
    formData.append("logtimes[][log_type]", LOG_TYPE.NOTE);
    formData.append("logtimes[][note]", ctx.sharedNote);
    appendedAnyLogtimeItem = true;
  }

  return {
    formData,
    hasLogtimePayload: appendedAnyLogtimeItem,
  };
}

export function remapFormKeysAfterJsonSave(
  dataBefore: LogTimeByDateResponse,
  dataAfter: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
): Record<string, unknown> {
  const rowsBefore = buildTrackingTimelineRows(dataBefore);
  const idMap = new Map<number, number>();
  const usedAfterIds = new Set<number>();
  const afterLogtimes = dataAfter.logtimes ?? [];

  for (const vRow of rowsBefore) {
    if (typeof vRow.id !== "number" || vRow.id >= 0) continue;
    const loggedAt = resolveFormLoggedAt(vRow.id, formValues);
    if (!loggedAt) continue;

    const targetMs = loggedAtToComparableMs(loggedAt);
    const match = afterLogtimes.find((lt) => {
      if (typeof lt.id !== "number" || lt.id <= 0 || usedAfterIds.has(lt.id)) {
        return false;
      }
      if (lt.log_type !== vRow.log_type) return false;
      const ltMs = loggedAtToComparableMs(trimmedOrNull(lt.logged_at));
      const timesMatch = ltMs != null && targetMs != null && ltMs === targetMs;
      return timesMatch;
    });

    if (match != null) {
      idMap.set(vRow.id, match.id);
      usedAfterIds.add(match.id);
    }
  }

  return remapFormKeysAfterCreate(formValues, idMap);
}

export function hasWorkerLogtimesFormChanges(
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
  canEditAllAttendanceFields?: boolean,
): boolean {
  if (canEditAllAttendanceFields) {
    if (hasWorkerLogtimesJsonTimeChanges(data, formValues)) {
      return true;
    }
    if (
      buildWorkerLogtimesSaveFormData(
        "",
        0,
        "",
        data,
        formValues,
        canEditAllAttendanceFields,
        { attachmentsOnly: true },
      ).hasLogtimePayload
    ) {
      return true;
    }
  } else if (
    buildWorkerLogtimesSaveFormData(
      "",
      0,
      "",
      data,
      formValues,
      canEditAllAttendanceFields,
    ).hasLogtimePayload
  ) {
    return true;
  }
  return hasVirtualLogtimesFormInput(data, formValues);
}
